import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw, X, PlugZap } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const midnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
  return d.toLocaleString("it-IT", midnight ? { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" } : { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const ago = (h) => (h == null ? "" : h < 24 ? "oggi" : h < 48 ? "ieri" : `${Math.floor(h / 24)} giorni fa`);

const STYLE = {
  fresh: { cls: "bg-emerald-50 border-emerald-300 text-emerald-900", icon: CheckCircle2, iconCls: "text-emerald-600" },
  stale: { cls: "bg-amber-50 border-amber-300 text-amber-900", icon: Clock, iconCls: "text-amber-600" },
  old: { cls: "bg-red-50 border-red-300 text-red-900", icon: AlertTriangle, iconCls: "text-red-600" },
  empty: { cls: "bg-amber-50 border-amber-300 text-amber-900", icon: AlertTriangle, iconCls: "text-amber-600" },
  error: { cls: "bg-red-50 border-red-300 text-red-900", icon: XCircle, iconCls: "text-red-600" },
  off: { cls: "bg-zinc-100 border-zinc-300 text-zinc-700", icon: PlugZap, iconCls: "text-zinc-500" },
};

// Alert in alto nelle pagine CRM: esito del controllo connessione + freschezza dati della fonte esterna (Elastic/Kibana)
export default function CrmFreshnessBanner() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(true);
  const [hidden, setHidden] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.get("/elastic/freshness"); setR(data); }
    catch (e) { setR({ state: "error", message: "Controllo fonte esterna non riuscito" }); }
    finally { setBusy(false); }
  };
  useEffect(() => { check(); }, []);
  if (hidden) return null;
  if (busy && !r) {
    return <div className="rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm text-zinc-600 flex items-center gap-2" data-testid="crm-freshness-checking"><RefreshCw className="h-4 w-4 animate-spin" /> Verifica connessione alla fonte esterna…</div>;
  }
  const s = STYLE[r.state] || STYLE.error;
  const Icon = s.icon;
  const job = r.last_job;
  return (
    <div role="status" className={`rounded-xl border px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${s.cls}`} data-testid="crm-freshness-banner" data-state={r.state}>
      <Icon className={`h-5 w-5 shrink-0 ${s.iconCls}`} />
      <span className="font-semibold" data-testid="crm-freshness-message">{r.message}</span>
      {r.connected && (
        <span className="text-xs opacity-80" data-testid="crm-freshness-details">
          Ultimo scontrino: <b>{fmt(r.last_receipt_at)}</b>{r.age_hours != null && <> ({ago(r.age_hours)})</>}
          {" · "}registrati oggi: <b>{r.receipts_today}</b> · fonte raggiunta in {r.latency_ms} ms
        </span>
      )}
      {job && <span className="text-xs opacity-80" data-testid="crm-freshness-job">Ultimo import: {fmt(job.finished_at || job.started_at)} ({job.status === "done" ? "ok" : job.status === "error" ? "errore" : job.status})</span>}
      <span className="ml-auto flex items-center gap-1">
        <button type="button" onClick={check} disabled={busy} className="p-1 rounded-md hover:bg-black/5 disabled:opacity-50" aria-label="Ricontrolla" data-testid="crm-freshness-refresh"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /></button>
        <button type="button" onClick={() => setHidden(true)} className="p-1 rounded-md hover:bg-black/5" aria-label="Chiudi" data-testid="crm-freshness-close"><X className="h-4 w-4" /></button>
      </span>
    </div>
  );
}
